// @purpose VIEW
// /the-idea/ interactive layer (templates/consumer/howitworks/page.html). The server renders the whole
// story; this turns it into one decision at a time: a docked copy of the team grid where all twelve react at
// once, the visitor's own reactions (engine.js, same maths as the server), watch mode, the rewind and the
// funnel events. Fictional choices are only ever sent as the anonymous `sim_step` event.
import { track } from "../track.js";
import { BANDS, run, step } from "./engine.js";

function init() {
  const root = document.querySelector("[data-hw]");
  const raw = document.getElementById("hw-data");
  if (!root || !raw || root.dataset.hwReady) return;
  root.dataset.hwReady = "1";
  const data = JSON.parse(raw.textContent);
  const { k, cast, decisions } = data;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const scrollTo = (el) => el && el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });

  const sections = [...root.querySelectorAll("[data-hw-dec]")];
  const end = root.querySelector("[data-hw-end]");
  const dock = root.querySelector("[data-hw-dock]");
  const grid = root.querySelector("[data-hw-grid]");
  const progress = root.querySelector("[data-hw-progress]");
  const watchBtn = root.querySelector("[data-hw-watch]");

  const path = [];
  const carry = cast.map(() => 0);
  const history = [];
  let started = false;
  let watching = false;

  root.classList.add("hw--js");
  if (watchBtn) watchBtn.hidden = false;
  sections.forEach((s, i) => {
    s.hidden = i > 0;
    s.querySelector("[data-hw-react]").hidden = true;
    s.querySelectorAll("[data-hw-opt]").forEach((b) => (b.disabled = false));
  });
  if (end) end.hidden = true;

  // The live grid: a compact copy of the team, docked at the top of the story while the visitor decides.
  const live = grid.cloneNode(true);
  live.removeAttribute("data-hw-grid");
  live.classList.add("hw-grid--live");
  dock.appendChild(live);
  const members = new Map([...live.querySelectorAll("[data-hw-member]")].map((m) => [m.dataset.hwMember, m]));

  function begin() {
    if (started) return;
    started = true;
    dock.hidden = false;
    track("sim_step", { action: "start" });
  }

  function setProgress(n) {
    if (!progress) return;
    progress.hidden = false;
    progress.textContent = progress.dataset.label.replace("{n}", String(n));
  }

  function paint(row, d) {
    cast.forEach((c, j) => {
      const r = row[j];
      const m = members.get(c.id);
      if (!m) return;
      m.dataset.band = BANDS[r.shown];
      m.dataset.hidden = r.band !== r.shown ? "1" : "";
      m.style.setProperty("--out", Math.min(1, r.output).toFixed(2));
      m.style.setProperty("--residue", Math.min(1, r.carry).toFixed(2));
      m.querySelector("[data-hw-chip]").textContent = d.chips[c.name][r.shown];
    });
  }

  function react(section, d, key, row) {
    const box = section.querySelector("[data-hw-react]");
    box.querySelector("[data-hw-react-head]").textContent = `${key}, ${d.labels[key]}`;
    const list = box.querySelector("[data-hw-chips]");
    list.replaceChildren(
      ...cast.map((c, j) => {
        const li = document.createElement("li");
        li.className = "hw-chips__item";
        li.dataset.band = BANDS[row[j].shown];
        const name = document.createElement("span");
        name.className = "hw-chips__name";
        name.textContent = c.name;
        const text = document.createElement("span");
        text.className = "hw-chips__text";
        text.textContent = d.chips[c.name][row[j].shown];
        li.append(name, " ", text);
        return li;
      }),
    );
    const spots = box.querySelector("[data-hw-spots]");
    spots.replaceChildren(
      ...d.spotlights.map((s) => {
        const j = cast.findIndex((c) => c.name === s.name);
        const p = document.createElement("p");
        p.className = "hw-spot";
        const b = document.createElement("strong");
        b.textContent = `${s.name}.`;
        p.append(b, " ", s.lines[row[j].shown]);
        return p;
      }),
    );
    box.hidden = false;
  }

  function choose(n, key) {
    if (path.length !== n - 1) return; // one call per decision, in order
    begin();
    const d = decisions[n - 1];
    const v = d.options[key];
    const row = cast.map((ch, j) => {
      const r = step(ch, v, Number(d.duration || 0), carry[j], k);
      carry[j] = r.carry;
      return r;
    });
    path.push(key);
    history.push(row);
    const section = sections[n - 1];
    section.querySelectorAll("[data-hw-opt]").forEach((b) => {
      b.disabled = true;
      b.setAttribute("aria-pressed", b.dataset.hwOpt === key ? "true" : "false");
    });
    paint(row, d);
    react(section, d, key, row);
    setProgress(n);
    track("sim_step", { action: "decide", n, option: key });
  }

  function showEnd() {
    end.hidden = false;
    dock.classList.add("hw-dock--done");
    const list = end.querySelector("[data-hw-path-list]");
    if (list && path.length) {
      list.replaceChildren(
        ...path.map((key, i) => {
          const li = document.createElement("li");
          li.textContent = `${decisions[i].n}. ${key}, ${decisions[i].labels[key]}`;
          return li;
        }),
      );
      end.querySelector("[data-hw-path]").hidden = false;
    }
    const rewind = end.querySelector("[data-hw-rewind]");
    if (rewind && path.length === decisions.length) rewind.hidden = false;
    track("sim_step", { action: "complete" });
    const reveal = end.querySelector("[data-hw-reveal]");
    if (reveal && "IntersectionObserver" in window) {
      const io = new IntersectionObserver((es) => {
        if (es.some((e) => e.isIntersecting)) {
          track("sim_step", { action: "reveal" });
          io.disconnect();
        }
      });
      io.observe(reveal);
    }
  }

  function next(n) {
    if (n < sections.length) {
      sections[n].hidden = false;
      scrollTo(sections[n]);
    } else {
      showEnd();
      scrollTo(end);
    }
  }

  function rewind(kind) {
    const start = kind === "exhausting" ? data.exhausting : {};
    const replay = run(cast, decisions, path, k, start);
    const mine = history;
    const carried = (rows, j) => rows.filter((r) => r[j].band === 2).length;
    const out = end.querySelector("[data-hw-rewind-list]");
    out.replaceChildren(
      ...cast.map((c, j) => {
        const li = document.createElement("li");
        const a = carried(mine, j);
        const b = carried(replay, j);
        li.dataset.change = String(b - a);
        const name = document.createElement("strong");
        name.textContent = c.name;
        li.append(name, ` ${a} → ${b} of 7`);
        return li;
      }),
    );
    end.querySelector("[data-hw-rewind-out]").hidden = false;
    track("sim_step", { action: "rewind", detail: kind });
  }

  const wait = (ms) => new Promise((r) => setTimeout(r, reduce ? Math.min(ms, 300) : ms));
  async function watch() {
    if (watching) return;
    watching = true;
    track("sim_step", { action: "watch" });
    begin();
    for (let i = path.length; i < decisions.length && watching; i += 1) {
      scrollTo(sections[i]);
      await wait(1200);
      if (!watching) return;
      choose(i + 1, data.watch[i]);
      await wait(2200);
      if (!watching) return;
      next(i + 1);
      await wait(600);
    }
    watching = false;
  }

  root.addEventListener("click", (e) => {
    const opt = e.target.closest("[data-hw-opt]");
    if (opt) {
      if (e.isTrusted) watching = false;
      choose(Number(opt.closest("[data-hw-dec]").dataset.hwDec), opt.dataset.hwOpt);
      return;
    }
    const nx = e.target.closest("[data-hw-next]");
    if (nx) {
      e.preventDefault();
      next(Number(nx.closest("[data-hw-dec]").dataset.hwDec));
      return;
    }
    if (e.target.closest("[data-hw-begin], [data-hw-start]")) begin();
    if (e.target.closest("[data-hw-watch]")) watch();
    const rw = e.target.closest("[data-hw-rewind-go]");
    if (rw) rewind(rw.dataset.hwRewindGo);
    const route = e.target.closest("[data-hw-route]");
    if (route) track("sim_step", { action: "route", detail: route.dataset.hwRoute });
  });
}

init();
document.addEventListener("htmx:afterSettle", init);
